/**
 * ProductsContext - Manages dynamic product loading from Tebex
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { loadProducts as loadProductsData } from '@/data/productsData';

const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

const slugifyName = (value) => {
  if (!value) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const normalizeProduct = (product) => {
  if (!product) return product;
  const existingSlug = product.slug ? String(product.slug).trim() : '';
  if (existingSlug) return product;
  const fallbackSlug = slugifyName(product.title || product.name) || String(product.id || '');
  return fallbackSlug ? { ...product, slug: fallbackSlug } : product;
};

const normalizeProductsData = (data) => {
  const normalizeList = (list) => (list || []).map(normalizeProduct);
  return {
    products: normalizeList(data.products),
    carouselProducts: normalizeList(data.carouselProducts),
    allProducts: normalizeList(data.allProducts),
    iapProducts: normalizeList(data.iapProducts),
    bundleProducts: normalizeList(data.bundleProducts),
    filters: data.filters || ['All Products']
  };
};

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [carouselProducts, setCarouselProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [iapProducts, setIapProducts] = useState([]);
  const [bundleProducts, setBundleProducts] = useState([]);
  const [filters, setFilters] = useState(['All Products']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productsCurrency, setProductsCurrency] = useState(null);

  // Load products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await loadProductsData();

        const normalized = normalizeProductsData(data);
        setProducts(normalized.products);
        setCarouselProducts(normalized.carouselProducts);
        setAllProducts(normalized.allProducts);
        setIapProducts(normalized.iapProducts);
        setBundleProducts(normalized.bundleProducts);
        setFilters(normalized.filters);
        // Extract currency from first product for cart validation
        const currency = normalized.products?.[0]?.currency || 'USD';
        setProductsCurrency(currency);
      } catch (err) {
        console.error('Failed to load products:', err);
        setError(err);
        // Set empty arrays on error
        setProducts([]);
        setCarouselProducts([]);
        setAllProducts([]);
        setIapProducts([]);
        setBundleProducts([]);
        setFilters(['All Products']);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Refresh products (can be called manually if needed)
  const refreshProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await loadProductsData();

      const normalized = normalizeProductsData(data);
      setProducts(normalized.products);
      setCarouselProducts(normalized.carouselProducts);
      setAllProducts(normalized.allProducts);
      setIapProducts(normalized.iapProducts);
      setBundleProducts(normalized.bundleProducts);
      setFilters(normalized.filters);
      // Extract currency from first product for cart validation
      const currency = normalized.products?.[0]?.currency || 'USD';
      setProductsCurrency(currency);
    } catch (err) {
      console.error('Failed to refresh products:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter products by category
  const filterProducts = useCallback((filter) => {
    if (filter === 'All Products') return allProducts;
    return allProducts.filter(product => product.category === filter);
  }, [allProducts]);

  // Find product by ID
  const findProductById = useCallback((id) => {
    const numericId = parseInt(id, 10);
    const normalizedId = String(id).toLowerCase();
    return allProducts.find(product => (
      product.id === numericId ||
      String(product.id) === String(id) ||
      (product.slug && product.slug === normalizedId)
    ));
  }, [allProducts]);

  const getBundleItems = useCallback((product) => {
    if (!product?.isBundle) return [];
    if (Array.isArray(product.bundleItems) && product.bundleItems.length > 0) {
      return product.bundleItems;
    }

    const itemIds = product.bundleItemIds || [];
    return itemIds
      .map(itemId => allProducts.find(item => Number(item.tebexPackageId || item.id) === Number(itemId)))
      .filter(Boolean);
  }, [allProducts]);

  const getProductBundles = useCallback((product) => {
    if (!product) return [];
    if (Array.isArray(product.bundleMemberships) && product.bundleMemberships.length > 0) {
      return product.bundleMemberships;
    }

    const productPackageId = Number(product.tebexPackageId || product.id);
    return bundleProducts
      .filter(bundle => (bundle.bundleItemIds || []).includes(productPackageId))
      .map(bundle => ({
        id: bundle.tebexPackageId || bundle.id,
        key: bundle.bundleKey || null,
        label: bundle.bundleLabel || bundle.title,
        product: bundle
      }));
  }, [bundleProducts]);

  const catalogStale = allProducts.some(product => (
    product?.catalogStale === true || product?.commerce?.stale === true
  ));

  const value = {
    products,
    carouselProducts,
    allProducts,
    iapProducts,
    bundleProducts,
    filters,
    loading,
    error,
    catalogStale,
    productsCurrency,
    refreshProducts,
    filterProducts,
    findProductById,
    getBundleItems,
    getProductBundles
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};
